/**
 * Audit Log screen — append-only history of every admin + fleet action.
 *
 * Two-tab layout (Overview / Log Entries) matching the legacy view, with
 * three improvements over what shipped on app.nurev.io:
 *   - `fleet` and `fleet_compliance` categories are now selectable (the
 *     legacy CATEGORIES array missed them, so fleet events were filterable
 *     server-side but not from the UI).
 *   - IP column added on the table for security forensics — useful when
 *     auditing a balance adjustment or component edit traced back to a
 *     specific session.
 *   - Details column shows a structured row of key:value pairs with the
 *     full JSON on hover (was a single truncated string in the legacy).
 */
import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  AuditCategory,
  AuditLogEntry,
  AuditAdminRef,
  AuditLogListParams
} from '@/api/auditLog';
import { useAuditLogs, useAuditStats } from '@/hooks/useAuditLog';
import {
  Card,
  Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDateTime, fmtNum } from '@/lib/format';

const CATEGORIES: Array<AuditCategory | 'all'> = [
  'all',
  'auth',
  'user',
  'device',
  'payout',
  'component',
  'system',
  'support',
  'fleet',
  'fleet_compliance'
];

const CATEGORY_BADGE: Record<string, string> = {
  auth: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  user: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  device: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  payout: 'bg-amber-500/30 text-amber-200 ring-1 ring-amber-500/40',
  component: 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30',
  system: 'bg-white/10 text-nurev-muted ring-1 ring-white/10',
  support: 'bg-pink-500/20 text-pink-300 ring-1 ring-pink-500/30',
  fleet: 'bg-indigo-500/20 text-indigo-300 ring-1 ring-indigo-500/30',
  fleet_compliance: 'bg-rose-500/20 text-rose-300 ring-1 ring-rose-500/30'
};

function CategoryBadge({ category }: { category?: AuditCategory | string }) {
  const cls =
    CATEGORY_BADGE[category || ''] ||
    'bg-white/10 text-nurev-muted ring-1 ring-white/10';
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {category || '—'}
    </span>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading } = useAuditStats();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Total Log Entries"
          value={fmtNum(data?.totalLogs)}
          loading={isLoading}
        />
        <StatCard
          label="Last 24 Hours"
          value={fmtNum(data?.last24h)}
          color="text-emerald-300"
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>Last 7 Days by Category</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !data?.byCategory?.length ? (
            <p className="text-sm text-nurev-muted">
              No activity in the last 7 days.
            </p>
          ) : (
            <div className="space-y-2">
              {data.byCategory.map(c => (
                <div
                  key={c._id || 'null'}
                  className="flex items-center justify-between"
                >
                  <CategoryBadge category={c._id || undefined} />
                  <span className="font-mono text-sm text-nurev-text">
                    {fmtNum(c.count)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <SectionLabel>Last 7 Days by Admin</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !data?.byAdmin?.length ? (
            <p className="text-sm text-nurev-muted">
              No activity in the last 7 days.
            </p>
          ) : (
            <div className="space-y-2">
              {data.byAdmin.map(a => (
                <div
                  key={a._id || 'system'}
                  className="flex items-center justify-between"
                >
                  <span className="text-xs text-nurev-text">
                    {a._id || 'system'}
                  </span>
                  <span className="font-mono text-sm text-nurev-text">
                    {fmtNum(a.count)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Log table ────────────────────────────────────────────────────────────

function LogTable() {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState<AuditCategory | 'all'>('all');
  const [actionFilter, setActionFilter] = useState('');

  const params = useMemo<AuditLogListParams>(
    () => ({
      page,
      limit: 50,
      category: category === 'all' ? '' : category,
      action: actionFilter.trim()
    }),
    [page, category, actionFilter]
  );
  const { data, isLoading } = useAuditLogs(params);
  const docs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {CATEGORIES.map(c => (
          <button
            key={c}
            onClick={() => {
              setCategory(c);
              setPage(1);
            }}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
              category === c
                ? 'bg-amber-500 text-nurev-bg'
                : 'bg-white/[0.06] text-nurev-muted hover:bg-white/10'
            }`}
          >
            {c === 'all' ? 'All' : c}
          </button>
        ))}
        <input
          type="text"
          value={actionFilter}
          onChange={e => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
          placeholder="Filter by action (regex)…"
          className="w-44 rounded-full border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-xs text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none"
        />
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Action</th>
                <th className="px-4 py-3 font-medium">Admin</th>
                <th className="px-4 py-3 font-medium">IP</th>
                <th className="px-4 py-3 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : docs.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No audit log entries match these filters.
                  </td>
                </tr>
              ) : (
                docs.map(log => <LogRow key={log._id} log={log} />)
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

function LogRow({ log }: { log: AuditLogEntry }) {
  const adminEmail =
    log.adminEmail ||
    (typeof log.adminId === 'object' && log.adminId
      ? (log.adminId as AuditAdminRef).email
      : null) ||
    'system';

  const detailsSummary = log.details
    ? Object.entries(log.details)
        .slice(0, 3)
        .map(([k, v]) => {
          const display =
            typeof v === 'object' ? JSON.stringify(v) : String(v);
          return `${k}: ${display.length > 40 ? `${display.slice(0, 40)}…` : display}`;
        })
        .join('  ·  ')
    : '—';

  return (
    <tr className="border-b border-nurev-border/50 hover:bg-white/[0.02]">
      <td className="whitespace-nowrap px-4 py-3 text-xs text-nurev-muted">
        {fmtDateTime(log.createdAt)}
      </td>
      <td className="px-4 py-3">
        <CategoryBadge category={log.category} />
      </td>
      <td className="px-4 py-3 font-mono text-xs text-nurev-text">
        {log.action}
      </td>
      <td className="px-4 py-3 text-xs text-nurev-muted-dim">{adminEmail}</td>
      <td className="px-4 py-3 font-mono text-[11px] text-nurev-muted">
        {log.ip || '—'}
      </td>
      <td className="max-w-[400px] px-4 py-3 text-xs text-nurev-muted">
        <span
          className="block truncate"
          title={log.details ? JSON.stringify(log.details, null, 2) : ''}
        >
          {detailsSummary}
        </span>
      </td>
    </tr>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'logs', label: 'Log Entries' },
  { id: 'overview', label: 'Overview' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function AuditLogScreen() {
  const [tab, setTab] = useState<Tab>('logs');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Audit Log
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Append-only history of every admin action. Auth, user, device,
            payout, component, system, support, fleet, fleet-compliance.
          </p>
        </div>
        <a
          href="https://app.nurev.io/audit-log"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`rounded-md px-4 py-2 text-xs font-medium transition ${
              tab === t.id
                ? 'bg-amber-500 text-nurev-bg'
                : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'logs' && <LogTable />}
      {tab === 'overview' && <OverviewTab />}
    </div>
  );
}
